import { useState } from "react";
import { Link, Navigate, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  ChevronsLeft,
  ChevronsRight,
  FileText,
  LayoutDashboard,
  LogOut,
  Mail,
  MessageSquare,
  Palette,
  Plug,
  ScrollText,
  ShieldCheck,
  Sparkles,
  Trophy,
  Users,
} from "lucide-react";
import { useAdminAuth } from "../context/AdminAuthContext";
import { Button } from "./ui/button";

/** Gates every /admin/* route (except /admin/login) behind an admin session. */
export function AdminProtectedRoute() {
  const { isAuthenticated, loading } = useAdminAuth();
  const location = useLocation();
  if (loading) return null;
  if (!isAuthenticated) return <Navigate to="/admin/login" state={{ from: location }} replace />;
  return <AdminLayout />;
}

// Sidebar items — order matches the vertical stack.
const NAV_ITEMS = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, testId: "admin-nav-overview", end: true },
  { to: "/admin/members", label: "Members", icon: Users, testId: "admin-nav-members" },
  { to: "/admin/requests", label: "Requests", icon: Mail, testId: "admin-nav-requests" },
  { to: "/admin/admins", label: "Admins", icon: ShieldCheck, testId: "admin-nav-admins" },
  { to: "/admin/taxonomy", label: "Taxonomy", icon: Sparkles, testId: "admin-nav-taxonomy" },
  { to: "/admin/insurer-kpis", label: "Insurer KPIs", icon: Trophy, testId: "admin-nav-insurer-kpis" },
  { to: "/admin/reminder-templates", label: "Reminder templates", icon: MessageSquare, testId: "admin-nav-reminders" },
  { to: "/admin/pages", label: "Pages", icon: FileText, testId: "admin-nav-pages" },
  { to: "/admin/settings", label: "Integrations", icon: Plug, testId: "admin-nav-settings" },
  { to: "/admin/branding", label: "Branding", icon: Palette, testId: "admin-nav-branding" },
  { to: "/admin/audit-log", label: "Audit log", icon: ScrollText, testId: "admin-nav-audit" },
];

function SidebarItem({ item, collapsed }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      data-testid={item.testId}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
          isActive
            ? "bg-gold/10 text-gold shadow-[inset_2px_0_0_0_theme(colors.gold)]"
            : "text-muted hover:bg-elevated hover:text-text"
        }`
      }
      title={collapsed ? item.label : undefined}
    >
      <Icon className="h-4 w-4 shrink-0" />
      {!collapsed && <span className="truncate">{item.label}</span>}
    </NavLink>
  );
}

function AdminLayout() {
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  async function handleLogout() {
    await logout();
    navigate("/admin/login", { replace: true });
  }

  return (
    <div className="flex min-h-screen bg-bg">
      <aside
        className={`sticky top-0 flex h-screen shrink-0 flex-col border-r border-gold/20 bg-card/80 backdrop-blur transition-[width] duration-200 ${
          collapsed ? "w-16" : "w-60"
        }`}
        data-testid="admin-sidebar"
      >
        {/* Brand */}
        <Link
          to="/admin"
          className="flex items-center gap-2 border-b border-border px-4 py-4"
          data-testid="admin-brand"
        >
          <img src="/logo-mark.png" alt="" className="h-7 w-7 shrink-0 object-contain" />
          {!collapsed && (
            <div className="flex flex-col">
              <span className="font-serif text-sm leading-tight text-gold">The Money Gurukul</span>
              <span className="text-[10px] uppercase tracking-widest text-muted">Admin · Control Room</span>
            </div>
          )}
        </Link>

        {/* Nav */}
        <nav className="flex flex-1 flex-col gap-1 overflow-y-auto p-3">
          {NAV_ITEMS.map((item) => (
            <SidebarItem key={item.to} item={item} collapsed={collapsed} />
          ))}
        </nav>

        {/* Footer: signed-in + logout + collapse */}
        <div className="border-t border-border p-3">
          {!collapsed && (
            <div className="mb-2 text-[11px] text-muted" data-testid="admin-signed-in">
              <div className="truncate text-text">{admin?.name}</div>
              <div>{admin?.is_superadmin ? "Superadmin" : "Admin"}</div>
            </div>
          )}
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              data-testid="admin-logout"
              className="flex-1 justify-start gap-2 text-muted hover:text-text"
            >
              <LogOut className="h-4 w-4" />
              {!collapsed && <span>Log out</span>}
            </Button>
            <button
              onClick={() => setCollapsed((c) => !c)}
              className="rounded-md p-1.5 text-muted hover:text-gold"
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              data-testid="admin-sidebar-toggle"
            >
              {collapsed ? <ChevronsRight className="h-4 w-4" /> : <ChevronsLeft className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden">
        <div className="mx-auto max-w-6xl px-6 py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
